import dayjs from 'dayjs';
import { OnProjectStatus } from 'model/on-project-status';
import { ProjectPerson, ProjectPersonInterval } from 'model/project-person';
import { Project, ProjectWithIntervals } from 'model/project';
import { dayJsDate, dayKey, localeDateToMidday } from 'utils/date';
import { intervalsOverlap } from './intervals';

/* Sorts a list of project-person entries by their date */
export const sortProjectPersonEntriesChronologically = (
  personProjectEntries: ProjectPerson[],
  descending: boolean,
) => {
  const sortedProjectPersonEntries = [...personProjectEntries];
  sortedProjectPersonEntries.sort((entryA: ProjectPerson, entryB: ProjectPerson) => (
    descending ? (
      entryB.valid_from.getTime() - entryA.valid_from.getTime()
    ) : (
      entryA.valid_from.getTime() - entryB.valid_from.getTime()
    )
  ));
  return sortedProjectPersonEntries;
};

export const sortProjectPersonIntervalEntriesChronologically = (
  personProjectEntries: ProjectPersonInterval[],
  descending: boolean,
) => {
  const sortedProjectPersonEntries = [...personProjectEntries];
  sortedProjectPersonEntries.sort((entryA: ProjectPerson, entryB: ProjectPerson) => (
    descending ? (
      entryB.valid_from.getTime() - entryA.valid_from.getTime()
    ) : (
      entryA.valid_from.getTime() - entryB.valid_from.getTime()
    )
  ));
  return sortedProjectPersonEntries;
};

/* Extracts the people that are working on a project during a specific day */
export const extractCurrentlyWorkingPeopleFromProject = (
  projectId: string,
  projectPersonsEntries: ProjectPerson[],
  currentDay: Date,
) => {
  const activeSlotsInProject: Set<string> = new Set();
  const projPersUntilToday = projectPersonsEntries.filter((pp) => pp.project_id === projectId && pp.valid_from <= currentDay);
  const projPersOrdered = sortProjectPersonEntriesChronologically(projPersUntilToday, false);

  projPersOrdered.forEach((projectPerson) => {
    const ppInProject = activeSlotsInProject.has(projectPerson.person_id);
    if (!ppInProject && projectPerson.status !== OnProjectStatus.INACTIVE) {
      activeSlotsInProject.add(projectPerson.person_id);
    } else if (ppInProject && (projectPerson.status === OnProjectStatus.INACTIVE || projectPerson.utilisation === 0)) {
      activeSlotsInProject.delete(projectPerson.person_id);
    }
  });

  return activeSlotsInProject.size;
};

/*
 * Extracts the total FTE (Full-Time Equivalent) of people working on a project during a specific day
 * Returns the sum of all active utilisations divided by 100 (e.g., 150% utilisation = 1.5 FTE)
 */
export const extractUtilisationOfCurrentlyWorkingPeopleFromProject = (
  projectId: string,
  projectPersonsEntries: ProjectPerson[],
  currentDay: Date,
): number => {
  const activeSlotsInProject: Map<string, number> = new Map();
  const projPersUntilToday = projectPersonsEntries.filter(
    (pp) => pp.project_id === projectId && pp.valid_from <= currentDay,
  );
  const projPersOrdered = sortProjectPersonEntriesChronologically(projPersUntilToday, false);

  projPersOrdered.forEach((projectPerson) => {
    const isInactive = projectPerson.status === OnProjectStatus.INACTIVE || projectPerson.utilisation === 0;

    if (isInactive) {
      activeSlotsInProject.delete(projectPerson.person_id);
    } else {
      activeSlotsInProject.set(projectPerson.person_id, projectPerson.utilisation);
    }
  });

  return Array.from(activeSlotsInProject.values()).reduce((total, utilisation) => total + utilisation, 0) / 100;
};

/*
 * Reduces a list of \ref ProjectPerson entries to a list of \ref ProjectPersonInterval
 *
 * ProjectPersonInterval is self-contained and doesn't require information from other entries
 * in the list to be information-complete
 */
export const reduceProjectPersonToIntervals = (
  personProjectEntries: ProjectPerson[],
): ProjectPersonInterval[] => {
  const intervals: ProjectPersonInterval[] = [];

  // Sort project person entries by start date
  const sortedProjectPersonEntries = sortProjectPersonEntriesChronologically(
    personProjectEntries,
    false,
  );

  sortedProjectPersonEntries.forEach((projectPerson: ProjectPerson, index: number) => {
    // Skip entries that have the "inactive" status - they represent the end of a person's
    // involvement in a project and do not translate to an interval
    if (projectPerson.status === OnProjectStatus.INACTIVE) {
      return;
    }

    let intervalEndDate;
    // Loop through subsequent entries of the list (since it's sorted by date
    // these will all be future entries) until we find the next entry with the same project/person
    // The start date of that entry represents the end date of the current entry
    for (let i = index + 1; i < sortedProjectPersonEntries.length; i += 1) {
      const nextProjectPerson = sortedProjectPersonEntries[i];
      if (nextProjectPerson.project_id === projectPerson.project_id
        && nextProjectPerson.person_id === projectPerson.person_id) {
        // The next event's valid_from is the first day NOT working
        // The interval's end_date should be the LAST DAY of work (subtract 1 day)
        const nextEventDate = dayJsDate(nextProjectPerson.valid_from);
        nextEventDate.setDate(nextEventDate.getDate() - 1);
        intervalEndDate = nextEventDate;
        break;
      }
    }

    const interval: ProjectPersonInterval = { ...projectPerson, end_date: intervalEndDate };
    intervals.push(interval);
  });
  return intervals;
};

/*
 * Given a \ref ProjectPerson entry, returns the next \ref ProjectPerson entry
 * (among a list of sorted candidate entries) where the person & project ID match
 */
const getNextProjectPersonEntryByPersonAndProject = (
  entry: ProjectPerson,
  personProjectEntries: ProjectPerson[],
) => {
  const startEntryIndex = personProjectEntries.findIndex(
    (candidateEntry) => candidateEntry.sort_key === entry.sort_key,
  );

  if (startEntryIndex !== -1 && startEntryIndex < personProjectEntries.length - 1) {
    for (let i = startEntryIndex + 1; i < personProjectEntries.length; i += 1) {
      const candidateEntry = personProjectEntries[i];
      if (candidateEntry.project_id === entry.project_id
        && candidateEntry.person_id === entry.person_id) {
        return candidateEntry;
      }
    }
  }
  return undefined;
};

/*
 * Given a \ref ProjectPerson entry, returns the next \ref ProjectPerson entry
 * (among a list of candidate entries) of the same person & same project
 */
export const getNextProjectPersonChronologicalEntry = (
  entry: ProjectPerson,
  personProjectEntries: ProjectPerson[],
): ProjectPerson | undefined => {
  // Sort project person entries by start date (ascending)
  const sortedProjectPersonEntries = sortProjectPersonEntriesChronologically(
    personProjectEntries,
    false,
  );
  return getNextProjectPersonEntryByPersonAndProject(entry, sortedProjectPersonEntries);
};

/*
 * Given a \ref ProjectPerson entry, returns the previous \ref ProjectPerson entry
 * (among a list of candidate entries) of the same person & same project
 */
export const getPreviousProjectPersonChronologicalEntry = (
  entry: ProjectPerson,
  personProjectEntries: ProjectPerson[],
): ProjectPerson | undefined => {
  // Sort project person entries by start date (descending)
  const sortedProjectPersonEntries = sortProjectPersonEntriesChronologically(
    personProjectEntries,
    true,
  );
  return getNextProjectPersonEntryByPersonAndProject(entry, sortedProjectPersonEntries);
};

/*
 * Splits a list of project person intervals into two lists:
 * - One with all intervals that include the specified date
 * - One with all intervals that do not include the specified date
 */
export const intervalIncludesDate = (interval: ProjectPersonInterval, date: Date) => {
  if (date > interval.valid_from) {
    if (interval.end_date === undefined || interval.end_date === null || date < interval.end_date) {
      return true;
    }
  }
  return false;
};

/*
 * Filters a list of project-person entries for a given person and a given time interval
 *
 * \param personId             The ID of the person we want to filter for
 * \param projectPersonEntries The relevant project-person entries for this person
 * \param startDate            The start date of the queried interval
 * \param endDate              The end date of the queried interval
 */
export const getPersonProjectsInInterval = (
  personId: string,
  startDate: Date,
  endDate: Date | undefined,
  intervals: ProjectPersonInterval[],
): ProjectPersonInterval[] => intervals.filter((interval) => {
  const queriedTimeInterval = { start: startDate, end: endDate };
  const projectPersonTimeInterval = { start: interval.valid_from, end: interval.end_date };
  return interval.person_id === personId
    && intervalsOverlap(queriedTimeInterval, projectPersonTimeInterval);
});

/*
 * Returns the average utilisation (in %) of a list of project-person intervals
 * The average is computed only taking into account periods with active project-person intervals
 * Any timeframes not covered by intervals are ignored for the calculation of the average
 *
 * \param intervals            The project-person intervals for which we want to find the average
 * \param cutoffEndDate        A cut-off date to consider as the max end date for intervals
 *                             without a defined end date
 */
export const getIntervalsAvgUtilisation = (
  intervals: ProjectPersonInterval[],
  cutoffEndDate: Date,
): number => {
  if (intervals.length > 0) {
    let intervalLengthAccumulator = 0;
    const weightedUtilisation = intervals.reduce((accumulator, interval) => {
      const intervalStart = interval.valid_from.getTime();
      let intervalEnd = cutoffEndDate.getTime();
      if (interval.end_date !== undefined && interval.end_date !== null) {
        intervalEnd = interval.end_date.getTime();
      }
      const intervalLength = intervalEnd - intervalStart;
      intervalLengthAccumulator += intervalLength;
      return accumulator + (interval.utilisation * intervalLength);
    }, 0);
    return weightedUtilisation / intervalLengthAccumulator;
  }
  return 0;
};

/*
 * Returns the average utilisation (in %) of a list of project-person intervals
 * during a given timeframe. Any time period in the queried interval not covered
 * by a project-person interval will effectively be considered to have 0% utilisation
 *
 * \param intervals            The project-person intervals for which we want to find the average
 * \param startDate            The start date of the queried interval
 * \param endDate              The end date of the queried interval
 */
export const getAvgUtilisationInInterval = (
  intervals: ProjectPersonInterval[],
  startDate: Date,
  endDate: Date,
): number => {
  if (intervals.length > 0) {
    const queryIntervalStart = startDate.getTime();
    const queryIntervalEnd = endDate.getTime();
    const queryIntervalLength = queryIntervalEnd - queryIntervalStart;

    const weightedUtilisation = intervals.reduce((accumulator, interval) => {
      const intervalStart = Math.max(interval.valid_from.getTime(), queryIntervalStart);
      let intervalEnd = queryIntervalEnd;
      if (interval.end_date !== undefined && interval.end_date !== null) {
        intervalEnd = Math.min(interval.end_date.getTime(), queryIntervalEnd);
      }
      const intervalLength = intervalEnd - intervalStart;
      return accumulator + (interval.utilisation * intervalLength);
    }, 0);
    return weightedUtilisation / queryIntervalLength;
  }
  return 0;
};

/*
 * Returns the average utilisation (in %) of a person in a given interval
 *
 * \param personId             The ID of the person we want to filter for
 * \param projectPersonEntries The relevant project-person entries for this person
 * \param startDate            The start date of the queried interval
 * \param endDate              The end date of the queried interval
 */
export const getPersonAvgUtilisationInInterval = (
  personId: string,
  startDate: Date,
  endDate: Date,
  projectPersonIntervals: ProjectPersonInterval[],
): number => {
  const activeIntervals = getPersonProjectsInInterval(
    personId,
    startDate,
    endDate,
    projectPersonIntervals,
  );
  return getAvgUtilisationInInterval(activeIntervals, startDate, endDate);
};

/*
 * Returns the IDs of the projects a person worked on between two given dates
 *
 * \param personId             The ID of the person we want to filter for
 * \param projectPersonEntries The relevant project-person entries for this person
 * \param startDate            The start date of the queried interval
 * \param endDate              The end date of the queried interval
 */
export const getPersonProjectIdsInInterval = (
  personId: string,
  startDate: Date,
  endDate: Date | undefined,
  projectPersonEntriesIntervals: ProjectPersonInterval[],
): string[] => {
  const activeIntervals = getPersonProjectsInInterval(
    personId,
    startDate,
    endDate,
    projectPersonEntriesIntervals,
  );
  const projectIds = activeIntervals.map((interval) => interval.project_id);

  // Filter unique project IDs - needed to prevent duplicate project IDs in the
  // result if multiple intervals with the same project ID match the query interval
  return projectIds.filter((value, index, array) => array.indexOf(value) === index);
};

/*
 * Returns the ID of a project a person was working on a given date.
 *
 * \param personId             The ID of the person we want to filter for
 * \param projectPersonIntervals The relevant project-person entries for this person
 * \param date                 The point in time for which we want to get the active project
 */
export const getPersonActiveProjectId = (
  personId: string,
  projectPersonIntervals: ProjectPersonInterval[],
  date: Date,
): string[] | undefined => {
  const projectIds = getPersonProjectIdsInInterval(personId, date, date, projectPersonIntervals);
  return projectIds.length > 0 ? projectIds : undefined;
};

/*
 * Returns the IDs of the projects a group of people were working on a given date.
 *
 * \param personId             The ID of the person we want to filter for
 * \param projectPersonEntries The relevant project-person entries for this person
 * \param date                 The point in time for which we want to get the active project
 */
export const getPeopleActiveProjectIds = (
  peopleIds: string[],
  projectPersonIntervals: ProjectPersonInterval[],
  date: Date,
): string[] | undefined => {
  const projectIds: string[] = [];
  peopleIds.forEach((personId) => {
    const personProjectIds = getPersonProjectIdsInInterval(personId, date, date, projectPersonIntervals);
    projectIds.push(...personProjectIds);
  });

  return projectIds;
};

export type ProjectsByDate = { 'active': ProjectWithIntervals[], 'past': ProjectWithIntervals[] };

export const getPastAndActiveProjects = (projects: Project[], projectWorkIntervals: ProjectPersonInterval[]): ProjectsByDate => {
  // Load the relevant projects references in the project-person entries
  // Nest project-person entries into their relevant project
  // and split by active / non-active projects
  const today = dayJsDate();
  const { active, past: other } = projects.reduce((acc, project) => {
    let atLeastOneIntervalIncludesDate = false;
    const projectWithIntervals = {
      ...project,
      intervals: projectWorkIntervals.filter((interval) => {
        if (interval.project_id === project.id) {
          if (!atLeastOneIntervalIncludesDate && intervalIncludesDate(interval, today)) {
            atLeastOneIntervalIncludesDate = true;
          }
          return true;
        }
        return false;
      }),
      avg_utilisation: 0,
    };

    projectWithIntervals.avg_utilisation = getIntervalsAvgUtilisation(
      projectWithIntervals.intervals,
      today,
    );

    if (atLeastOneIntervalIncludesDate) {
      acc.active.push(projectWithIntervals);
    } else {
      acc.past.push(projectWithIntervals);
    }

    return acc;
  }, { active: [], past: [] } as ProjectsByDate);

  return { active, past: other };
};

/*
 * Returns the default start date to use for a newly created project person interval
 *
 * \param rates                         The existing rate intervals on the list we are editing
 * \param firstIntervalDefaultStartDate The date to return if the rates list is empty
 */
export const getDefaultProjectPersonIntervalStartDate = (
  projectPersonEvents: ProjectPersonInterval[],
  firstIntervalDefaultStartDate: Date | null,
): Date => {
  if (projectPersonEvents.length > 0) {
    const lastRateInterval = projectPersonEvents[projectPersonEvents.length - 1];
    if (lastRateInterval.end_date != null) {
      // Default the newly added rate interval start date to be the
      // end date of the previous one (plus one day)
      const dayAfterLastRateIntervalEnd = dayJsDate(lastRateInterval.end_date);
      dayAfterLastRateIntervalEnd.setDate(lastRateInterval.end_date.getDate() + 1);
      return dayAfterLastRateIntervalEnd;
    }
    // If a previous rate interval exists and it has no end date,
    // our best estimate for a good default is the current date
    return localeDateToMidday(dayjs()) as Date;
  }
  if (firstIntervalDefaultStartDate) {
    return firstIntervalDefaultStartDate;
  }
  return localeDateToMidday(dayjs()) as Date;
};

/**
 * Improved inverse of reduceProjectPersonToIntervals.
 * For each interval, add the main event. If end_date exists, add an INACTIVE event at end_date+1ms
 * only if there is no next interval for the same person/project/slot at that time.
 */
export function expandIntervalsToProjectPersons(
  intervals: ProjectPersonInterval[],
  originalEvents?: ProjectPerson[],
): ProjectPerson[] {
  const result: ProjectPerson[] = [];
  // Group intervals by person_id and project_id ONLY
  const groups = new Map<string, ProjectPersonInterval[]>();
  intervals.forEach((interval) => {
    const key = `${interval.person_id}__${interval.project_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(interval);
  });

  // Build fast lookups for original events if provided
  let inactiveEventMap: Map<string, ProjectPerson> | undefined;
  let activeEventMap: Map<string, ProjectPerson> | undefined;
  if (originalEvents) {
    inactiveEventMap = new Map();
    activeEventMap = new Map();
    originalEvents.forEach((ev) => {
      // Day-level key — picker outputs LOCAL noon, loadDate produces LOCAL
      // midnight; getTime() comparison would miss matches across that
      // boundary and re-create the same logical event on every save.
      const k = `${ev.person_id}__${ev.project_id}__${dayKey(ev.valid_from)}`;
      if (ev.status === OnProjectStatus.INACTIVE) {
        inactiveEventMap!.set(k, ev);
      } else {
        // For non-INACTIVE events, store by content key so we can reuse server sort_keys
        // across saves without needing to track them in the interval's own sort_key field.
        activeEventMap!.set(k, ev);
      }
    });
  }

  Array.from(groups.values()).forEach((groupIntervals) => {
    // Sort intervals by valid_from
    const sorted = [...groupIntervals].sort((a, b) => a.valid_from.getTime() - b.valid_from.getTime());
    sorted.forEach((interval, i) => {
      // Emit the active event (copy all fields except end_date)
      const { end_date: endDate, ...eventFields } = interval;
      // Reuse the server-assigned sort_key for events that already exist on the server.
      // This prevents treating an already-saved event as a new create on subsequent saves.
      const activeKey = `${interval.person_id}__${interval.project_id}__${dayKey(interval.valid_from)}`;
      const existingActiveEvent = activeEventMap?.get(activeKey);
      result.push({
        ...eventFields,
        sort_key: existingActiveEvent?.sort_key ?? eventFields.sort_key,
      });
      if (endDate) {
        // The interval's end_date represents the LAST DAY of work (what the user sees/selects in the UI)
        // The INACTIVE event should be on the FIRST DAY NOT working (end_date + 1 day)
        const endDatePlusOne = dayJsDate(endDate);
        endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
        const inactiveValidFrom = endDatePlusOne;
        const nextInterval = sorted[i + 1];

        // Only skip INACTIVE event if next interval starts at exactly the same time (no gap)
        // This happens when a person moves between projects on the same day
        const shouldSkipInactive = nextInterval
          && nextInterval.status === OnProjectStatus.ON_PROJECT
          && dayKey(nextInterval.valid_from) === dayKey(inactiveValidFrom);

        if (!shouldSkipInactive) {
          let inactiveEvent: ProjectPerson | undefined;
          if (inactiveEventMap) {
            const k = `${interval.person_id}__${interval.project_id}__${dayKey(inactiveValidFrom)}`;
            inactiveEvent = inactiveEventMap.get(k);
          }
          if (inactiveEvent) {
            result.push({ ...inactiveEvent });
          } else {
            const { sort_key: sortKey, ...inactiveFields } = eventFields;
            result.push({
              ...inactiveFields,
              valid_from: inactiveValidFrom,
              status: OnProjectStatus.INACTIVE,
              utilisation: 0,
              sort_key: '',
            });
          }
        }
      }
    });
  });
  result.sort((a, b) => a.valid_from.getTime() - b.valid_from.getTime());
  return result;
}

// Utility function to generate a stable hash for a project person event
export function hashProjectPersonEvent(event: ProjectPersonInterval & { temp_id?: string; random?: number }): string {
  // If the event has a sort_key, use it as a stable key (for saved events)
  if (event.sort_key && event.sort_key !== '') {
    return `ppev-${event.sort_key}`;
  }
  // For unsaved events, use temp_id if present
  if (event.temp_id) {
    return `ppev-temp-${event.temp_id}`;
  }
  // Fallback: generate a hash from fields (should not happen if temp_id is always set)
  const base = `vf:${event.valid_from ? dayJsDate(event.valid_from).getTime() : ''}|ed:${event.end_date ? dayJsDate(event.end_date).getTime() : ''}|st:${event.status}|ut:${event.utilisation}|nd:${event.notes || ''}|id:${event.invoice_description || ''}|rnd:${event.random || ''}`;
  // Simple hash function (djb2)
  let hash = 5381;
  for (let idx = 0; idx < base.length; idx += 1) {
    hash = (hash * 33) + base.charCodeAt(idx);
  }
  return `ppev-new-${hash}`;
}

/**
 * Rewrite ON_PROJECT intervals' `end_date` to the project's *effective* end for
 * forward availability prediction, using the project's soft `likely_end_date`.
 * Mirrors the backend `engagement_as_of` rules so the frontend capacity/conflict
 * panels agree with bench_read / available_people_read.
 *
 * Strictly opt-in — only projects that have a `likely_end_date` are touched,
 * everything else is returned unchanged:
 *  - open-ended interval (no `end_date`): capped at `likely_end_date`.
 *  - interval ending on/after the project's nominal `end_date` (kept to the
 *    end): extended to `likely_end_date` (expected renewal / awaiting SOW).
 *  - interval ending before the nominal end (person left early): unchanged.
 *
 * Non-ON_PROJECT intervals (e.g. SUGGESTED pitch load) are returned unchanged.
 */
export function applyLikelyEndToIntervals(
  intervals: ProjectPersonInterval[],
  projectById: Map<string, Project>,
): ProjectPersonInterval[] {
  return intervals.map((interval) => {
    if (interval.status !== OnProjectStatus.ON_PROJECT) return interval;
    const project = projectById.get(interval.project_id);
    const likely = project?.likely_end_date;
    if (!project || !likely) return interval;
    if (interval.end_date == null) {
      return { ...interval, end_date: likely };
    }
    const nominalEnd = project.end_date ?? null;
    // Kept to (or past) the nominal project end → treat as an expected renewal.
    if (nominalEnd && !dayjs(interval.end_date).isBefore(dayjs(nominalEnd), 'day')) {
      return { ...interval, end_date: likely };
    }
    return interval;
  });
}
